import React from 'react';
import { Image as AntImage, ImageProps as AntImageProps } from 'antd';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';

export interface ImageProps extends AntImageProps {
  className?: string;
}

const useStyles = createUseStyles((theme: ThemeType) => ({
  image: {
    '& .ant-image': {
      borderRadius: theme.radius.sm,
      border: `${theme.border.width.sm} solid ${theme.color.semantic.border.subtle}`,
      backgroundColor: theme.color.semantic.background.neutral.subtle,
    },
    '& .ant-image-img': {
      borderRadius: theme.radius.sm,
    },
    '& .ant-image-mask': {
      borderRadius: theme.radius.sm,
      '& .ant-image-mask-info': {
        color: theme.color.semantic.text.inverse,
        '& .anticon': {
          color: theme.color.semantic.text.inverse,
        },
      },
    },
    '& .ant-image:hover .ant-image-mask': {
      border: `${theme.border.width.sm} solid ${theme.color.semantic.border.primary}`,
    },
    '& .ant-image-placeholder': {
      backgroundColor: theme.color.semantic.background.neutral.subtle,
      borderRadius: theme.radius.sm,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& .ant-image-placeholder-icon': {
        color: theme.color.semantic.text.soft,
        fontSize: theme.typography.fontSize.xl,
      },
    },
    '& .ant-image-error': {
      backgroundColor: theme.color.semantic.background.neutral.soft,
      borderRadius: theme.radius.sm,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      padding: theme.spacing[4],
      '& .ant-image-error-icon': {
        color: theme.color.semantic.brand.danger.primary,
        fontSize: theme.typography.fontSize.xl,
      },
    },
  },
  imagePreviewMask: {
    '& .ant-image-preview-wrap': {
      '& .ant-image-preview-img': {
        borderRadius: theme.radius.md,
        padding: theme.spacing[5],
      },
    },
    '& .ant-image-preview-operations': {
      backgroundColor: theme.color.semantic.background.neutral.strongL2,
      borderRadius: theme.radius.md,
      padding: theme.spacing[2],
      '& .ant-image-preview-operations-operation': {
        color: theme.color.semantic.text.inverse,
        '&:hover': {
          backgroundColor: theme.color.semantic.background.neutral.soft,
        },
      },
    },
    '& .ant-image-preview-switch-left, & .ant-image-preview-switch-right': {
      color: theme.color.semantic.text.inverse,
      backgroundColor: 'transparent',
      borderRadius: theme.radius.full,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: theme.color.semantic.background.neutral.soft,
      },
    },
    '& .ant-image-preview-close': {
      color: theme.color.semantic.text.inverse,
      backgroundColor: 'transparent',
      borderRadius: theme.radius.sm,
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        backgroundColor: theme.color.semantic.background.neutral.soft,
      },
    },
  },
}));

const ImageInner: React.FC<ImageProps> = ({
  className = '',
  preview = true,
  fallback,
  ...rest
}) => {
  const classes = useStyles();

  const combinedClassName = `${classes.image} ${classes.imagePreviewMask} ${className}`.trim();

  return (
    <AntImage
      className={combinedClassName}
      preview={preview}
      fallback={fallback}
      {...rest}
    />
  );
};

export const Image = Object.assign(ImageInner, {
  PreviewGroup: AntImage.PreviewGroup,
});

export default Image;
